import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  AppBar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  Stack,
  Icon,
  IconButton,
  TextField,
  Toolbar,
  Typography,
  Alert,
  AlertTitle,
} from "@mui/material";
import clsx from "clsx";
import { makeStyles, styled } from "@mui/styles";
import {
  DateFromEditorComponent,
  DateToEditorComponent,
} from "../../discount-master/DatepickerEditorComponent";
import jwtDecode from "jwt-decode";
import dayjs from "dayjs";
import AgGridTable from "../../utilities/AgGridTable";
import OAuthService from "src/app/auth/services/oAuthService";
import oAuthServiceConfig from "src/app/auth/services/oAuthService/oAuthServiceConfig";
import Select from "react-select";
import customStyles from "app/shared-components/SelectCustomStyle";
import ListPageHeader from "../../utilities/ListPageHeader";
import { confirmAlert } from "react-confirm-alert";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon/FuseSvgIcon";
import { toast } from "react-toastify";
import { grey } from "@mui/material/colors";
import AsyncSelect from "react-select/async";
import { LinearProgress } from "@material-ui/core";
import moment from "moment";
import CustomDatePicker from "../../utilities/CustomDatePicker";
import { useNavigate } from "react-router-dom";
import "./expenseClaim.css";

const url = `${process.env.REACT_APP_DESIGN_STUDIO_API_KEY_URL}${oAuthServiceConfig.displayMasters}`;
const rangeUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.findRangeByVendor}`; // ranges/search/findRangeByVendor
const priceGroupUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.priceGroup}`;
const factoryVendorUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.factoryVendor}`;
const uploadFileForDisplayMaster = `${process.env.REACT_APP_DESIGN_STUDIO_API_KEY_URL}${oAuthServiceConfig.uploadFileForDisplayMaster}`;
const dataUrl = `${process.env.REACT_APP_HR_API_KEY_URL}${oAuthServiceConfig.expenseClaimConfig}`;

const UploadButton = styled(Button)(({ theme }) => ({
  fontSize: 11,
  color: "#fff !important",
  background: `${theme.palette.primary.main}`,
  borderRadius: 20,
  padding: "5px 10px",
  cursor: "pointer",
}));

const Upload = (params) => {
  const downloadFile = `${process.env.REACT_APP_DESIGN_STUDIO_API_KEY_URL}${oAuthServiceConfig.downloadFile}`;
  const downloadPath = `${downloadFile}/${params.data?.uploadWinnerFile}?path=${params.data?.uploadWinnerPath}`;
  const hiddenFileInput = useRef();
  const [winner, setWinner] = useState({
    name: params.node.data.uploadWinnerFile,
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  async function remove() {
    // hiddenFileInput.current?.value = null;
    params.node.data.uploadWinnerFile = "";
    setWinner(null);
  }

  function uploadProgressChanged(params) {
    const { progress } = params;
    setUploadProgress(progress * 100);
  }
  const onChange = async (e) => {
    const fileUploaded = e.target.files[0];
    setUploading(true);
    if (!fileUploaded) return;
    const payload = {
      uploadWinnerFile: fileUploaded,
    };
    const response = await OAuthService.addFiles(
      `${uploadFileForDisplayMaster}?srNo=${params.data.srNo}`,
      payload,
      uploadProgressChanged
    );
    toast.success("Upload Successfully");
    params.node.updateData(response.data);
    setWinner(fileUploaded);
    setUploading(false);
    setUploadProgress(0);
  };

  const downloadFiles = async () => {
    const blob = await fetch(downloadPath, OAuthService.config()).then((resp) =>
      resp.blob()
    );
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = params.node?.data?.uploadWinnerFile;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(objUrl);
    document.body.removeChild(a);
  };

  return uploading ? (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box sx={{ width: "100%", mr: 1 }}>
        <LinearProgress variant="determinate" value={uploadProgress} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" color="text.secondary">
          {Math.round(uploadProgress)}%
        </Typography>
      </Box>
    </Box>
  ) : (
    <span>
      {!params.node.data.uploadWinnerFile && (
        <UploadButton
          variant="contained"
          component="label"
          fullWidth
          color="primary"
          ref={hiddenFileInput}
          onChange={onChange}
        >
          UPLOAD WINNER
          <input type="file" hidden />
        </UploadButton>
      )}
      {winner && (
        <>
          <Button
            variant="outlined"
            size="small"
            onClick={downloadFiles}
            startIcon={<FuseSvgIcon>heroicons-outline:download</FuseSvgIcon>}
          >
            Download
          </Button>
          <IconButton onClick={remove}>
            <FuseSvgIcon>heroicons-outline:x</FuseSvgIcon>
          </IconButton>
        </>
      )}
    </span>
  );
};

const currentDate = new Date();

export default function ExpenseClaimSettings(props) {
  const { open, handleClose } = props;
  const [rowData, setRowData] = useState([]);
  const [error, setError] = useState();
  const [snackState, setSnackState] = useState(false);
  const [successMsg, setSuccessMsg] = useState();
  const gridRef = useRef();
  const [addedRows, setAddedRows] = useState([]);
  const [productId, setProductId] = useState([]);
  const [checkDuplicate, setCheckDuplicate] = useState(false);
  const [configData, setConfigData] = useState([]);
  const [showPaginationDropdown, setShowPaginationDropdown] = useState(false);
  const [totalClaimed, setTotalClaimed] = useState();
  const [selectedCountry, setSelectedCountry] = useState();
  const navigate = useNavigate();
  const [deleteDisable, setDeleteDisable] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);

  const [docUpdate, setDocUpdate] = useState(false);
  const [initialState, setInitialState] = useState({
    docDate: currentDate,
    creationDate: new Date().toISOString(),
    employeeId: null,
    employeeName: null,
    companyCode: 1,
    companyName: "Nolte FZE",
    currency: "EUR",
    remark: null,
    financeDoc: "string",
    recordList: [],
  });

  const SelectCurrency = forwardRef((props, ref) => {
    const { data, colDef } = props;
    const refLocation = useRef();
    const [error, setError] = useState("");
    let [options, setOptions] = useState([
      { label: "AED", value: "AED" },
      { label: "EUR", value: "EUR" },
    ]);
    const [selectedOptions, setSelectedOptions] = useState(null);

    const handleOptionsChange = async (value) => {
      if (!value) return;
      props.node.setData(data);
      setSelectedOptions(value);
    };

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return selectedOptions?.label;
        },
      };
    });

    return (
      <div className="w-full" style={{ width: 200 }}>
        <Select
          className="basic-multi-select mb-4"
          name="name"
          required
          ref={refLocation}
          value={selectedOptions}
          onChange={handleOptionsChange}
          options={options}
          styles={customStyles()}
        />
      </div>
    );
  });

  const SelectCountry = forwardRef((props, ref) => {
    const { data, colDef } = props;
    const refCountries = useRef();
    const [countries, setCountries] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState(null);

    const loadCountries = async () => {
      let countryListUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.country}`;
  
      const dataList = await OAuthService.getList(
        countryListUrl + `?sort=name,asc&size=1000`
      );
  
      setCountries(
        dataList?.data?._embedded?.countries?.map((item) => ({
          label: `${item.isoCode} - ${item.name}`,
          value: item.isoCode,
        }))
      ); 
      return dataList?.data?._embedded?.countries?.map((item) => ({
        label: `${item.isoCode} - ${item.name}`,
        value: item.isoCode,
      }));
    };

    const handleOptionsChange = async (value) => {
      if (!value) return;
      props.node.setData(data);
      setSelectedOptions(value);
    };

    useEffect(() => {
      loadCountries();
    }, []);

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return selectedOptions?.label;
        },
      };
    });

    return (
      <div className="w-full" style={{ width: 200 }}>
        <Select
          className="basic-multi-select mb-4"
          name="name"
          ref={refCountries}
          value={selectedOptions}
          onChange={handleOptionsChange}
          options={countries}
          styles={customStyles()}
        />
      </div>
    );
  });

  const SelectCode = forwardRef((props, ref) => {
    const { data, colDef } = props;
    const refLocation = useRef();
    let [options, setOptions] = useState([
      { label: "52200-1-1 (Air Travel)", value: "Air Travel" },
      { label: "52200-1-2 (Hotel)", value: "Hotel" },
      { label: "52200-1-3 (Daily Allowance)", value: "Daily Allowance" },
      { label: "52200-1-4 (Visa Expenses)", value: "Visa Expenses" },
      { label: "52200-1-5 (Insurance Expenses)", value: "Insurance Expenses" },
      { label: "52200-1-6 (Client Welfare)", value: "Client Welfare" },
      { label: "52200-1-7 (Rent a Car)", value: "Rent a Car" },
      {
        label: "52200-1-8 (Petrol/Taxi Charges)",
        value: "Petrol/Taxi Charges",
      },
      { label: "52200-1-9 (Refreshment Exp)", value: "Refreshment Exp" },
      { label: "52200-1-10 (Communication)", value: "Communication" },
      { label: "52200-1-11 (Mobile)", value: "Mobile" },
      { label: "52200-1-12 (Parking)", value: "Parking" },
      { label: "52200-1-13 (Marketing)", value: "Marketing" },
      { label: "52200-1-14 (Training)", value: "Training" },
      { label: "52200-1-15 (Miscellenous)", value: "Miscellenous" },
    ]);
    const [selectedOptions, setSelectedOptions] = useState(null);

    const handleOptionsChange = async (value) => {
      if (!value) return;
      props.node.setData(data);
      props.data.subLedgerName = value.value;
      setSelectedOptions(value);
    };

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return selectedOptions?.label;
        },
      };
    });

    return (
      <div className="w-full" style={{ width: 200 }}>
        <Select
          className="basic-multi-select mb-4"
          name="name"
          required
          ref={refLocation}
          value={selectedOptions}
          onChange={handleOptionsChange}
          options={options}
          styles={customStyles()}
        />
      </div>
    );
  });

  const SelectPeriod = forwardRef((props, ref) => {
    const { data, colDef } = props;
    const refPeriod = useRef();
    const [error, setError] = useState("");
    let [options, setOptions] = useState([
      { label: "Day", value: "Day" },
      { label: "Week", value: "Week" },
      { label: "Month", value: "Month" },
    ]);
    const [selectedOptions, setSelectedOptions] = useState(null);

    const handleOptionsChange = async (value) => {
      if (!value) return;
      props.node.setData(data);
      setSelectedOptions(value);
      // setOpenCom(false);
    };

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return selectedOptions?.label;
        },
      };
    });

    return (
      <div className="w-full" style={{ width: 200 }}>
        <Select
          className="basic-multi-select mb-4"
          name="name"
          required
          ref={refPeriod}
          value={selectedOptions}
          onChange={handleOptionsChange}
          options={options}
          styles={customStyles()}
        />
      </div>
    );
  });

  const handleName = (params) => {
    return params.value;
  };

  const [columnDefs, setColumnDefs] = useState([
    {
      headerName: "Sno.",
      valueGetter: "node.rowIndex + 1",
      maxWidth: 100,
      editable: false,
    },
    {
      field: "countryName",
      headerName: "Country",
      cellEditorPopup: true,
      cellEditor: "agRichSelectCellEditor",
      cellEditor: SelectCountry,
      minWidth: 150,
    },
    {
      field: "subLedgerCode",
      headerName: "Expense Code",
      cellEditorPopup: true,
      cellEditor: "agRichSelectCellEditor",
      cellEditor: SelectCode,
      minWidth: 150,
    },
    {
      field: "subLedgerName",
      headerName: "Expense Type",
      minWidth: 150,
      editable: false,
      cellRenderer: handleName,
    },
    {
      field: "currency",
      headerName: "Currency",
      cellEditorPopup: true,
      cellEditor: "agRichSelectCellEditor",
      cellEditor: SelectCurrency,
      minWidth: 150,
    },
    {
      field: "maxAmount",
      headerName: "Max Amount",
      minWidth: 200,
    },
    {
      field: "period",
      headerName: "Period",
      cellEditorPopup: true,
      cellEditor: "agRichSelectCellEditor",
      cellEditor: SelectPeriod,
    },
  ]);

  const token = jwtDecode(localStorage.getItem("token"));

  const loadData = async () => {
    const claimConfig = `${process.env.REACT_APP_HR_API_KEY_URL}${oAuthServiceConfig.expenseClaimConfig}`;
    const configResult = await OAuthService.getList(claimConfig);
    const data = configResult?.data?._embedded?.expenseClaimConfigs;
    setRowData(data);
  };

  useEffect(() => {
    loadData()
  }, [])

  const onBtAdd = useCallback((event) => {
      let newRow = {};
      const newRowDate = gridRef.current.api.applyTransaction({ add: [newRow] });
      const RowIndex = newRowDate.add[0].rowIndex;      
      gridRef.current.api.refreshCells();
      setTimeout(() => {
        gridRef.current.api.setFocusedCell(RowIndex, "countryName");
        gridRef.current.api.startEditingCell({
          rowIndex: RowIndex,
          colKey: "countryName",
        });
      }, 500);
    },
    [rowData]
  );

  const onBtCopyRow = useCallback((event) => {
    const data = event.node.data;
    const copyRow = {
        countryName: data.countryName,
        countryName: data.countryName,
        subLedgerCode: data.subLedgerCode,
        subLedgerName: data.subLedgerName,
        maxAmount: data.maxAmount,
    }
    const newRowDate = gridRef.current.api.applyTransaction({ add: [copyRow] });
    const RowIndex = newRowDate.add[0].rowIndex;
    rowData.splice(RowIndex, 0, copyRow);
    gridRef.current.api.refreshCells();

    setTimeout(() => {
        gridRef.current.api.setFocusedCell(RowIndex, 'countryName')
        gridRef.current.api.startEditingCell({
            rowIndex: RowIndex,
            colKey: 'countryName',
        });
    }, 800);
  }, [rowData]);

  const onBtDeleteRow = useCallback(
    (event) => {
      const selectedRow = [event.node.data];

      confirmAlert({
        title: "Confirm to delete",
        message: "Are you sure to delete.",
        buttons: [
          {
            label: "Yes",
            onClick: () => {
              rowData?.splice(event.node.rowIndex, 1);
              gridRef?.current?.api.applyTransaction({ remove: selectedRow });
              selectedRow.map(async (row) => {
                // const dataUrl = `${process.env.REACT_APP_HR_API_KEY_URL}${oAuthServiceConfig.expenseClaimConfig}`;
                await OAuthService.deleteData(dataUrl, row.id);
                setSuccessMsg("Data deleted Successfully");
                setSnackState(true);
              });
            },
          },
          {
            label: "No",
            onClick: () => {},
          },
        ],
      });
    },
    [gridRef, rowData]
  );

  const getContextMenuItems = useCallback((event) => {
    const result = [
      {
        name: "Add",
        icon: '<span><i class="material-icons style="font-size: 18px">perm_identity</i></span>',
        action: () => {
          onBtAdd(event);
        },
      },
      {
        name: "Copy Row",
        icon: '<span><i class="material-icons" style="font-size: 18px">copy</i></span>',
        action: () => {
            onBtCopyRow(event);
        },
      },
      {
        name: "Delete",
        icon: '<span><i class="material-icons" style="font-size: 18px">delete</i></span>',
        action: () => {
          onBtDeleteRow(event);
        },
      },
      "separator",
      "export",
      // "chartRange",
    ];
    return result;
  }, []);

  const onRowValueChanged = async (event) => {
    const { rowIndex, data } = event.node;
    
    const isDuplicate = rowData.some(
      (row) =>
        row.countryName === data.countryName &&
        row.subLedgerCode === data.subLedgerCode
    );
    if (isDuplicate) {
      setCheckDuplicate(true)
      return;
    } else {
      setCheckDuplicate(false)
      const tempAddedRows = [...addedRows];
      tempAddedRows[rowIndex] = data;
      setAddedRows(tempAddedRows);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'Insert') {
      onBtAdd()
    }
  };

  const handleSubmit = async (props) => {
    const claimConfig = `${process.env.REACT_APP_HR_API_KEY_URL}${oAuthServiceConfig.expenseClaimConfig}`;
    addedRows.map((item, index) => {
      if (item !== undefined) {
        addedRows[index].countryCode = selectedCountry?.value;
        addedRows[index].countryName = selectedCountry?.label;

        OAuthService.addData(claimConfig, addedRows[index]);
      }
    });
    rowData?.length !== 0 && toast.success("Data Saved Successfully");
  };

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);

  const handleBack = () => {
    navigate("/expense-claim");
  }

  useEffect(() => {
    if (selectedRows.length > 0) {
        setDeleteDisable(false)
    } else {
        setDeleteDisable(true)
    }
  }, [selectedRows]);

  const onSelectionChanged = (params) => {
    setSelectedRows(params.api.getSelectedRows());
  }

  const handlePurge = (e) => {
    selectedRows.map(async (row) => {
        try {
            await OAuthService.deleteData(dataUrl, row.id);
            setSuccessMsg("Data deleted Successfully")
            setSnackState(true);
            loadData()
        } catch (error) {
            console.log("eror", error);
            setError(error.message);
        }
    })
  };

  return (
    <>
      <ListPageHeader
        componentName="Expense Config"
        pageName="Expense Config"
        showPaginationDropdown={showPaginationDropdown}
        error={error}
        successMsg={successMsg}
        snackState={snackState}
        handleSubmit={handleSubmit}
        handleBack={handleBack}
        handlePurge={handlePurge}
        deleteDisable={deleteDisable}
      />
      {checkDuplicate && <Alert severity="error" className="mb-16">
          <AlertTitle>Error</AlertTitle>
          {`Duplicate entry: Country and subLedgerCode combination already exists.`}
      </Alert>}
      <div className="h-full" onKeyDown={onKeyDown} tabIndex="0">
        <AgGridTable
          rowData={rowData}
          columnDefs={columnDefs}
          gridRef={gridRef}
          getContextMenuItems={getContextMenuItems}
          onCellValueChanged={onRowValueChanged}
          onSelectionChanged={onSelectionChanged}
        />
      </div>
    </>
  );
}
